import React, { useEffect, useState } from 'react'
import { GAtrack } from '../../services/ga'
import { useWalletContext } from '../WalletContext'
import BoxHeaderNew, { DEFAULT_SLIPPAGE } from '../BoxHeader/BoxHeader'
import PrimaryButton from '../PrimaryButton'
import { useStoreActions, useStoreState } from '../../model/hooks'
import { numFormat } from '../../blockchainUtils/math'
import { PositionsV3 } from '../PositionsV3'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { pageStyle, pageStyleMobile } from '../Main'
import Header from '../Header/Header'
import { tokenAddresses } from '../IncentivePage/IncentivePage.tsx'
import Popup from '../Popup/index.tsx'
import { TelegramIcon } from '../../svgIcons/TelegramIcon.tsx'
import { SuccessIcon } from '../../svgIcons/SuccessIcon.tsx'
import { PoolOverview, loadPoolOverviewData } from '../charts/chartsApi.ts'
import Footer from '../Footer/Footer.tsx'
import { Network } from '../../services/token.service.ts'

export const MAX_TICK = 887272

function PositionsPage() {
    let [searchParams] = useSearchParams()
    const { walletAddress: connectedAddress, networkName: network } = useWalletContext()
    const getToken = useStoreState((state) => state.tokens.getToken)
    const postions = useStoreState((state) => state.clPools.getCLPositions(network || ''))
    const [isLoadingPage, setIsLoadingPage] = useState(postions && postions.length > 0 ? false : true)
    const [isScrollToLegacy, setIsScrollToLegacy] = useState(false)
    const [isTelegramPopupActive, setIsTelegramPopupActive] = useState(false)
    const [didReadTelegrapPopup, setDidSelectReadTelegramPopup] = useState(false)
    const [aprOverview, setAprOverview] = useState({} as Record<string, { apr: number; incentiveApr: number }>)
    const loadPositons = useStoreActions((actions) => actions.clPools.loadPositons)
    const [selectedPositionPage, setSelectedPositionPage] = useState(-1)
    const [loadPositionsInterval, setLoadPositionsInterval] = useState(undefined as unknown as NodeJS.Timeout)
    const loadLegacyPositons = useStoreActions((actions) => actions.clPools.loadLegacyPositons)

    const navigate = useNavigate()

    useEffect(() => {
        loadOverview()
        if (searchParams.get('isLegacy')) {
            setIsScrollToLegacy(true)
            navigate('/positions')
        }
    }, [])

    useEffect(() => {
        if (network === 'mainnet-alpha') GAtrack('route_pools', 'route_pools', 'pools', -1)
    }, [])

    useEffect(() => {
        if (selectedPositionPage !== -1 && !searchParams.get('selectedPosition')) setSelectedPositionPage(-1)
        if (postions && postions.length > 0 && searchParams.get('selectedPosition')) setSelectedPositionPage(Number(searchParams.get('selectedPosition')))
    }, [window.location.href, postions])

    useEffect(() => {
        let interval: NodeJS.Timeout
        if (network && connectedAddress && selectedPositionPage === -1) {
            clearInterval(loadPositionsInterval)
            onLoadPostions()
            const positionsInterval = setInterval(() => {
                if (selectedPositionPage !== -1) return
                onLoadPostions(false)
            }, 60000)
            interval = positionsInterval
            setLoadPositionsInterval(positionsInterval)
        }
        if (network && connectedAddress && selectedPositionPage !== -1) {
            clearInterval(loadPositionsInterval)
            const positionsInterval2 = setInterval(() => {
                onLoadPostions(false, selectedPositionPage)
            }, 10000)
            interval = positionsInterval2
            setLoadPositionsInterval(positionsInterval2)
        }
        return () => {
            clearInterval(loadPositionsInterval)
            clearInterval(interval) // only for local env because everything is rendered twice
        }
    }, [network, connectedAddress, selectedPositionPage])

    async function onLoadPostions(isStartLoading = true, selectedPositionIndex?: number) {
        if (isStartLoading) setIsLoadingPage(true)
        try {
            await loadPositons({
                network: network as Network,
                address: connectedAddress!,
                selectedPosition: selectedPositionIndex !== undefined && selectedPositionIndex !== -1 ? selectedPositionIndex : -1
            })
            loadLegacyPositons({ network: network as Network })
            setIsLoadingPage(false)
        } catch (e) {
            setIsLoadingPage(false)
        }
    }

    async function loadOverview() {
        const ids = [
            '0x503f36ee47238549e73e691a840dca44530ed8a2dc98c9bd5adaaacec759536',
            '0x43b4920a31bfdbaa560a78b62c82435934af2b026305d11d0624d9fba40b5',
            '0x71273c5c5780b4be42d9e6567b1b1a6934f43ab8abaf975c0c3da219fc4d040',
            '0x7c5b7061e9e2e233b6ba9e084c3574c6575495721b18344c251d0234e1a77a3',
            '0x6466c43edbe6b3c35b5048efab935d946f2192ed55d9f89cd911eb88a867fb2',
            '0x4e584e93632aa496d5274e9383db819be58a08e82139ea5d1deac734073faa1',
            '0x2607fa508399dd986c402d1c1c645d00bdec98363120a319f75c6911b46c437'
        ]
        const calls = [] as Promise<PoolOverview | undefined>[]
        ids.map((id) => {
            calls.push(loadPoolOverviewData(id))
        })
        const response = await Promise.all(calls)
        const overView = {} as Record<string, { apr: number; incentiveApr: number }>
        response.map((item, index) => {
            if (!item) return
            overView[ids[index]] = { apr: item?.apr.apr_cl, incentiveApr: item?.apr.incentive_apr }
        })
        setAprOverview(overView)
    }

    function renderLegacyPositions() {
        return (
            <PositionsV3
                isScrollToView={isScrollToLegacy}
                isLegacy={true}
                isLoading={isLoadingPage}
                onAddPosition={() => navigate('/create-position')}
                onSelectPosition={(index: number) => setSelectedPositionPage(index)}
                // onAddPosition={() => setIsAddPosition(true)}
                network={network!}
            />
        )
    }

    function renderCLPositions() {
        return (
            <PositionsV3
                isScrollToView={false}
                isLoading={isLoadingPage}
                onSelectPosition={(index: number) => {
                    navigate(`/position?positionIndex=${index}&walletAddress=${connectedAddress}`)
                }}
                onAddPosition={() => {
                    const ename = 'mint_add_position_via_button'
                    GAtrack(ename, ename, ename)
                    navigate('/create-position')
                    // setIsCreatePoolPage(false)
                    // setStepNumber(1)
                    // setIsAddPosition(true)
                }}
                network={network!}
            />
        )
    }

    function renderAddLiquidityLine(token1Address: string, token2Address: string, fee = 0.05, poolKey: string) {
        const token1 = getToken('mainnet-alpha', token1Address)
        const token2 = getToken('mainnet-alpha', token2Address)
        if (!token1 || !token2) return null

        const incentiveApr = aprOverview[poolKey] && aprOverview[poolKey].incentiveApr ? aprOverview[poolKey].incentiveApr : 0
        const totalApr = aprOverview[poolKey] ? aprOverview[poolKey].apr + incentiveApr : 0
        return (
            <div className='bg-[#1D285A] flex p-4 rounded-[16px] flex items-center w-full justify-between my-2'>
                <div className='flex flex-col'>
                    <div className='flex items-center'>
                        <div className='flex items-center'>
                            <div>
                                <img src={token1.icon} alt='' width={24} height={24} />
                            </div>
                            <div className='relative left-[-10px]'>
                                <img src={token2.icon} alt='' width={24} height={24} />
                            </div>
                        </div>
                        <span className='text-white font-bold text-[18px]'>{`${token1!.symbol}/${token2?.symbol}`}</span>
                    </div>
                    <div style={{ color: 'rgba(255, 255, 255, 0.50)' }} className='mt-[12px] flex items-center'>
                        <div className='font-bold bg-[#0C3F6D] flex items-center h-[24px] rounded-[8px] p-2 '>
                            <span className='text-[12px] lg:text-[16px]'>Total APR:</span>
                            <span className='ml-1 text-[12px] lg:text-[16px] text-[#0AE8F9]'>{`${numFormat(totalApr.toFixed(2))}%`}</span>
                        </div>
                        <span className='text-[12px] ml-[10px]'>{`(${numFormat(incentiveApr.toFixed(2))}% Incentive)`}</span>
                    </div>
                </div>

                <PrimaryButton
                    onClick={() => {
                        const ename = 'mint_add_position_from_pools_head'
                        GAtrack(ename, ename, ename)
                        navigate(`/create-position?token0=${token1Address}&token1=${token2Address}&default_fee=${fee}`)
                    }}
                    size='sm'
                    theme='filled'>
                    <span>Add Liquidity</span>
                </PrimaryButton>
            </div>
        )
    }

    function renderAddLiquidityContainer() {
        if (network !== 'mainnet-alpha') return null

        return (
            <div className='flex-col max-w-[528px] w-full m-auto mb-10 mt-4'>
                <BoxHeaderNew className='' slippage={DEFAULT_SLIPPAGE} isRemoveSettings={true} name='Incentive Program'></BoxHeaderNew>

                <div className='mx-auto flex flex-col items-center my-[16px] max-w-[528px] w-full'>
                    <span className='mb-2 w-full'>
                        <span className='w-full text-[18px] text-white '>These pools participate in the</span>
                        <span onClick={() => navigate('/rewards')} className='cursor-pointer text-[#0AE8F9] underline w-full text-[18px] ml-2'>
                            Incentive Program
                        </span>
                    </span>
                    {renderAddLiquidityLine(tokenAddresses.ETH, tokenAddresses.USDC, 0.05, '0x71273c5c5780b4be42d9e6567b1b1a6934f43ab8abaf975c0c3da219fc4d040')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.USDC, tokenAddresses.USDT, 0.01, '0x7c5b7061e9e2e233b6ba9e084c3574c6575495721b18344c251d0234e1a77a3')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.STRK, tokenAddresses.ETH, 0.3, '0x503f36ee47238549e73e691a840dca44530ed8a2dc98c9bd5adaaacec759536')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.STRK, tokenAddresses.USDC, 0.3, '0x43b4920a31bfdbaa560a78b62c82435934af2b026305d11d0624d9fba40b5')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.WSTETH, tokenAddresses.ETH, 0.01, '0x6466c43edbe6b3c35b5048efab935d946f2192ed55d9f89cd911eb88a867fb2')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.USDC, tokenAddresses.DAI, 0.01, '0x2607fa508399dd986c402d1c1c645d00bdec98363120a319f75c6911b46c437')}
                    <div className='bg-[#0C3F6D80] opacity-[0.5] w-full h-[1px]' />
                    {renderAddLiquidityLine(tokenAddresses.WBTC, tokenAddresses.ETH, 0.05, '0x4e584e93632aa496d5274e9383db819be58a08e82139ea5d1deac734073faa1')}
                </div>
            </div>
        )
    }

    function renderPositions() {
        return (
            <>
                {renderAddLiquidityContainer()}
                {renderCLPositions()}
                {renderLegacyPositions()}
            </>
        )
    }

    function renderTelegramPopup() {
        if (!isTelegramPopupActive) return null
        const didRead = localStorage.getItem('didReadTelegram')
        if (didRead === 'true') return
        return (
            <Popup background='#151D44' contentClassName=' p-6 max-w-[528px] lg:w-[528px] w-full' onClose={() => setIsTelegramPopupActive(false)} open={true}>
                <>
                    <div className='flex items-start flex-col w-full '>
                        <BoxHeaderNew className='w-full items-start justify-start' isRemoveSettings={true} slippage='' name=''>
                            <div className='flex justify-start items-center text-[24px] text-white font-bold'>
                                <span className='mr-2'>mySwap Telegram Bot</span>
                                {/* <TelegramIcon className='mr-2 w-[20px] h-[20px]' />
                                <span>Telegram Bot</span> */}
                            </div>
                        </BoxHeaderNew>
                    </div>
                    <div className='flex items-center mt-[40px]'>
                        <span>
                            <span className='text-[18px] text-white font-600'>Get notified in real-time when your positions go out of range with the</span>
                            <span className='ml-2 text-[18px] text-[#0AE8F9] underline cursor-pointer' onClick={() => window.open('https://t.me/mySwapClBot')}>
                                mySwap Telegram Bot
                            </span>
                        </span>
                    </div>
                    <div className='mt-[40px] flex items-center'>
                        <div
                            onClick={() => setDidSelectReadTelegramPopup(!didReadTelegrapPopup)}
                            className={`w-4 h-4 border border-[1px] border-[${
                                didReadTelegrapPopup ? '#0AE8F9' : 'white'
                            }] rounded-[4px] cursor-pointer text-white flex justify-center items-center text-[#010620] ${
                                didReadTelegrapPopup ? 'bg-[#0AE8F9]' : 'bg-[transparent]'
                            }`}>
                            {didReadTelegrapPopup ? <SuccessIcon className='w-3 h-3' /> : null}
                        </div>
                        <span className='text-white ml-2 text-[14px]'>Don’t show this message again</span>
                    </div>
                    <div className='flex flex-col mt-[24px]'>
                        <PrimaryButton onClick={() => setIsTelegramPopupActive(!isTelegramPopupActive)} className='' theme='bordered'>
                            <span className='text-white'>No, thanks</span>
                        </PrimaryButton>
                        <PrimaryButton
                            onClick={() => {
                                window.open('https://t.me/mySwapClBot')
                                setIsTelegramPopupActive(!isTelegramPopupActive)
                                if (didReadTelegrapPopup) localStorage.setItem('didReadTelegram', 'true')
                            }}
                            className='mt-2'
                            theme='filled'>
                            <>
                                <TelegramIcon className='mr-2 w-[20px] h-[20px]' />
                                <span>Sign me up!</span>
                            </>
                        </PrimaryButton>
                    </div>
                </>
            </Popup>
        )
    }

    return (
        <div className='h-full relative pb-[48px] lg:pb-0'>
            <div className='lg:pt-6 max-w-[98%] m-auto'>
                <Header className='' selectedTab={'pools'} />
            </div>
            <div className={`pb-[65px] container w-full p-4 mx-auto`}>
                <div className='relative flex w-full flex-col'>{renderPositions()}</div>
            </div>
            <Footer selectedTab={'pools'} />
            <div style={pageStyle} className={`hidden lg:flex z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
            <div style={pageStyleMobile} className={`lg:hidden z-0 absolute h-full w-full bg-[#00176A] pointer-events-none`} />
            {renderTelegramPopup()}
        </div>
    )
}

export default PositionsPage
